import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { useWidgetMode, type WidgetMode } from './composables/widgetMode'
import './style.css'
import App from './App.vue'
import AppSteps from './AppSteps.vue'
import i18n, { syncLocale } from './i18n'
import AppCheckoutReminder from './AppCheckoutReminder.vue'

export interface OnwardSearcherConfig {
  mode?: WidgetMode
  stepsTarget?: string | Element
}

class OnwardSearcher {
  mount(id: string | Element, config?: OnwardSearcherConfig) {
    syncLocale()

    const target = typeof id === 'string' ? document.querySelector(id) : id
    if (!target) {
      throw new Error(`Target element not found for selector: ${id}`)
    }

    const modeFromAttr =
      target instanceof Element ? target.getAttribute('data-mode') : null

    const mode: WidgetMode =
      config?.mode ?? (modeFromAttr as WidgetMode) ?? 'default'

    if (mode === 'search') {
      const instanceId = `shared-${Date.now()}`
      const pinia = createPinia()

      const searchApp = createApp(App)
      searchApp.use(pinia)
      searchApp.use(i18n)
      useWidgetMode.provide({ mode: 'search', instanceId }, searchApp)
      searchApp.mount(target)

      const stepsTargetSelector =
        config?.stepsTarget ??
        (target instanceof Element ? target.getAttribute('data-steps-target') : null)

      let stepsEl: Element

      if (stepsTargetSelector) {
        const resolved =
          typeof stepsTargetSelector === 'string'
            ? document.querySelector(stepsTargetSelector)
            : stepsTargetSelector
        if (!resolved) throw new Error(`Steps target not found: ${stepsTargetSelector}`)
        stepsEl = resolved
      } else {
        const newEl = document.createElement('div')
        target.insertAdjacentElement('afterend', newEl)
        stepsEl = newEl
      }

      const appSteps = createApp(AppSteps)
      appSteps.use(pinia)
      appSteps.use(i18n)
      useWidgetMode.provide({ mode: 'steps', instanceId }, appSteps)
      appSteps.mount(stepsEl)
      return
    }

    if (mode === 'checkout') {
    const appCheckout = createApp(AppCheckoutReminder)
    appCheckout.use(createPinia())
    appCheckout.use(i18n)
    useWidgetMode.provide({ mode: 'checkout' }, appCheckout)
    appCheckout.mount(target)
    return
  }

    const app = createApp(App)
    app.use(createPinia())
    app.use(i18n)
    useWidgetMode.provide({ mode }, app)
    app.mount(target)
  }
}

if (window) {
  window.OnwardSearcher = OnwardSearcher
}

declare global {
  interface Window {
    OnwardSearcher: typeof OnwardSearcher
  }
}

export default OnwardSearcher